/**
 * Research SSE pipeline → store subscriber.
 *
 * Translates `research:*` typed events into department-store mutations.
 * Wired once at module init by `store/department/store.ts` after
 * `useDepartmentStore` is created.
 *
 * Logic mirrors what `pipelines/researchSSE.ts:handleEvent` previously
 * did via the `SSECallbacks` mutator bag — every transition in the old
 * `if (phase === ... && status === ...)` ladder maps to one event
 * subscription here.
 */

import { sseEvents } from "./sseEvents";
import { mapResearchDoneData } from "../store/department/utils";
import type {
  CampaignData,
  CoAgentMessage,
  StepStatus,
} from "../store/department/types";
import type { CollectionReport } from "@/config/coAgentPrompts";

interface ResearchSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  setCampaignName: (name: string) => void;
  setFocusedStep: (index: number) => void;
}

let wired = false;

/**
 * Idempotent subscriber registration. Caller passes a `getHooks` getter
 * that resolves to the live store mutators on every event — keeps the
 * subscription decoupled from store-construction order and lets
 * `useDepartmentStore.getState()` evolve without rebinding.
 */
export function wireResearchSubscriber(
  getHooks: () => ResearchSubscriberHooks,
): void {
  if (wired) return;
  wired = true;

  sseEvents.subscribe("research:session-named", ({ name }) => {
    getHooks().setCampaignName(name);
  });

  sseEvents.subscribe("research:queries-generated", ({ queryPlan, count }) => {
    const hooks = getHooks();
    hooks.addStepLog(1, `[SUCCESS] Generated ${count} research queries`);
    hooks.updateCampaignData({
      queryPlan: queryPlan as CampaignData["queryPlan"],
      researchPhase: "data_collection",
      researchProgress: {
        phase: "prompt_generation",
        completed: count,
        total: count,
      },
    });
  });

  sseEvents.subscribe("research:data-collection-progress", ({ track, completed, total }) => {
    getHooks().updateCampaignData({
      researchPhase: "data_collection",
      researchProgress: { phase: track, completed, total },
    });
  });

  sseEvents.subscribe("research:data-collection-complete", ({ data }) => {
    const hooks = getHooks();
    const successful = Number(data.successful ?? 0);
    const failed = Number(data.failed ?? 0);
    const durationMs = Number(data.duration_ms ?? 0);

    hooks.addStepLog(
      1,
      `[SUCCESS] Collected ${successful} results (${failed} failed) in ${Math.round(durationMs / 1000)}s`,
    );
    hooks.updateCampaignData({
      researchPhase: "distillation",
      collectionReport: {
        stats: data.collection_stats as CollectionReport["stats"],
        errors: data.collection_errors as CollectionReport["errors"],
        geo_matrix: data.geo_matrix as CollectionReport["geo_matrix"],
      },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 2,
      stepStatuses: { ...state.stepStatuses, 1: "completed", 2: "running" },
    }));
  });

  sseEvents.subscribe("research:distillation-complete", ({ findingsCount, sections }) => {
    const hooks = getHooks();
    hooks.addStepLog(
      2,
      `[SUCCESS] Distilled ${findingsCount} findings across ${sections} sections`,
    );
    hooks.addStepLog(3, "[SUCCESS] Competitor intelligence extracted");
    hooks.updateCampaignData({ researchPhase: "synthesis" });
    hooks.setStepState((state) => ({
      currentStepIndex: 4,
      stepStatuses: { ...state.stepStatuses, 2: "completed", 3: "completed", 4: "running" },
    }));
    hooks.setFocusedStep(2); // GEO visibility plan now available
  });

  sseEvents.subscribe("research:synthesis-complete", () => {
    const hooks = getHooks();
    hooks.addStepLog(4, "[SUCCESS] Value propositions generated");
    hooks.addStepLog(5, "[SUCCESS] Content strategy synthesized");
    hooks.addStepLog(6, "[SUCCESS] Ads plan generated");
    hooks.updateCampaignData({ researchPhase: "done" });
    hooks.setStepState((state) => ({
      currentStepIndex: 6,
      stepStatuses: { ...state.stepStatuses, 4: "completed", 5: "completed", 6: "completed" },
    }));
    hooks.setFocusedStep(6); // Ads plan (last auto-completed step) ready
  });

  sseEvents.subscribe("research:done", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      researchStatus: "complete",
      researchPhase: "done",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...mapResearchDoneData(data as Record<string, any>),
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 7,
      stepStatuses: { ...state.stepStatuses, 7: "waiting" },
    }));
    hooks.setFocusedStep(7); // Launch summary ready
    const geoMatrix = (data as Record<string, unknown>).geo_matrix as
      | { prompts_tested?: number }
      | undefined;
    hooks.addMessage({
      role: "agent",
      content: `Research complete! I tested your brand across ${geoMatrix?.prompts_tested || 0} queries on 3 AI engines. Here's your GEO visibility breakdown:`,
      widget: "geoPlan",
    });
  });

  sseEvents.subscribe("research:error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({ researchStatus: "error", researchPhase: "error" });
    hooks.addStepLog(1, `[ERROR] ${message}`);
    hooks.setStepState((state) => ({
      currentStepIndex: 0,
      stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
    }));
    hooks.addMessage({
      role: "agent",
      content: `Research encountered an error: ${message}`,
    });
  });
}
